/**
 * How the user entered the packages funnel, carried on the URL from the flight
 * review page through to package checkout and read by every package event.
 *
 * The URL is the whole mechanism: the funnel already preserves these params
 * across its own rewrites, clones them into the new tab the hotel list opens,
 * and again into the checkout URL. That makes the attribution survive refreshes
 * and back/forward, and end by itself when the user leaves the funnel — no
 * storage to keep in sync and nothing to clear.
 */

import { RBC_PACKAGES_QUERY_PARAM } from "@tenant/types";

export const UP_SELL_PRICE_TAB_PARAM = "up_sell_price_tab";
export const UP_SELL_TRIP_ID_PARAM = "up_sell_trip_id";

/** Params to carry through funnel URL rewrites. */
export const PACKAGE_UP_SELL_PARAMS = [
  RBC_PACKAGES_QUERY_PARAM.upSell,
  UP_SELL_PRICE_TAB_PARAM,
  UP_SELL_TRIP_ID_PARAM,
];

export enum PackageEntrySource {
  flight_up_sell = "flight_up_sell",
  package_search = "package_search",
}

export type PackageUpSellAttribution = {
  entry_source: PackageEntrySource;
  /** Pricing mode of the up-sell flight. */
  rbc_price_tab?: "fixed" | "flex";
  /** Trip id of the up-sell flight, joining back to the flight shop. */
  flight_trip_id?: string;
};

/** Writes the attribution onto the URL the up-sell navigates to. */
export const setPackageUpSellParams = (
  params: URLSearchParams,
  {
    rbc_price_tab,
    flight_trip_id,
  }: Omit<PackageUpSellAttribution, "entry_source">
): void => {
  params.set(RBC_PACKAGES_QUERY_PARAM.upSell, "true");
  if (rbc_price_tab) params.set(UP_SELL_PRICE_TAB_PARAM, rbc_price_tab);
  if (flight_trip_id) params.set(UP_SELL_TRIP_ID_PARAM, flight_trip_id);
};

const parsePriceTab = (value: string | null): "fixed" | "flex" | undefined =>
  value === "fixed" || value === "flex" ? value : undefined;

/**
 * The attribution to attach to package events. Defaults to `package_search` —
 * an explicit default, so a missing property (not yet instrumented) stays
 * distinguishable from a traditional package booking.
 */
export const parsePackageUpSellAttribution = (
  search: string | URLSearchParams
): PackageUpSellAttribution => {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  if (params.get(RBC_PACKAGES_QUERY_PARAM.upSell) !== "true") {
    return { entry_source: PackageEntrySource.package_search };
  }

  const rbcPriceTab = parsePriceTab(params.get(UP_SELL_PRICE_TAB_PARAM));
  const flightTripId = params.get(UP_SELL_TRIP_ID_PARAM);

  return {
    entry_source: PackageEntrySource.flight_up_sell,
    ...(rbcPriceTab ? { rbc_price_tab: rbcPriceTab } : {}),
    ...(flightTripId ? { flight_trip_id: flightTripId } : {}),
  };
};
